/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { ResourceAssignmentForRegionSwagger, ResourceMetadataSwagger } from 'src/core/services/resourceAssignmentSwaggers';
import { ResourceListResults } from 'src/core/types/ResourceListTypes';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { PublishJobCreateSwagger } from 'src/customText/core/services/deploy/customTextDeploySwaggers';
import { DeployJobSwagger, DeploymentSwagger, NewDeploymentSwagger } from 'src/modules/modelDeploy/types/deploymentSwaggers';

export const getCluDeployModelService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment({ apiName: 'clu' });

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    const getUrlBuilder = (projectName?: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .queryParam('api-version', LuisConstants.Clu.AuthoringApiVersion);

    const deploy = async ({
        projectName,
        deploymentName,
        operationHandle,
        trainedModelName,
        assignedResourceIds
    }: {
        projectName: string;
        deploymentName: string;
        trainedModelName: string;
        assignedResourceIds: string[];
        operationHandle: OperationHandler<PublishJobCreateSwagger>;
    }) => {
        const deploymentSwagger: NewDeploymentSwagger = { trainedModelLabel: trainedModelName, assignedResourceIds };

        const { headers: deployJobHeaders } = await httpService.put<PublishJobCreateSwagger>(
            getUrlBuilder(projectName)
                .resource('deployments', deploymentName)
                .build(),
            deploymentSwagger,
            apiOptions
        );

        const jobUrl = (<Record<string, string>>deployJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];
        await createOperationCall(jobUrl, apiOptions, operationHandle);
    };

    const getDeployment = async (projectName: string, deploymentName: string) => {
        const url = getUrlBuilder(projectName)
            .resource('deployments', deploymentName)
            .build();

        const { data: deployment } = await httpService.get<DeploymentSwagger>(url, apiOptions);

        return deployment;
    };

    const getDeployments = async (nextLink: string, projectName: string) => {
        const url =
            nextLink ||
            getUrlBuilder(projectName)
                .resource('deployments')
                .build();

        const { data: deployments } = await httpService.get<ResourceListResults<DeploymentSwagger>>(url, apiOptions);

        return deployments;
    };

    const swapDeployments = async ({
        projectName,
        operationHandle,
        sourceDeploymentName,
        targetDeploymentName
    }: {
        projectName: string;
        sourceDeploymentName: string;
        targetDeploymentName: string;
        operationHandle: OperationHandler<DeployJobSwagger>;
    }) => {
        const url = getUrlBuilder(projectName)
            .resource('deployments', ':swap')
            .build();

        const { headers: deploySwapJobHeaders } = await httpService.post<DeployJobSwagger>(
            url,
            { firstDeploymentName: sourceDeploymentName, secondDeploymentName: targetDeploymentName },
            apiOptions
        );

        const jobUrl = (<Record<string, string>>deploySwapJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];
        await createOperationCall(jobUrl, apiOptions, operationHandle);
    };

    const deleteDeployment = async ({
        projectName,
        deploymentName,
        operationHandle
    }: {
        projectName: string;
        deploymentName: string;
        operationHandle: OperationHandler<DeployJobSwagger>;
    }) => {
        const url = getUrlBuilder(projectName)
            .resource('deployments', deploymentName)
            .build();

        const { headers: deleteDeploymentJobHeaders } = await httpService.delete<DeployJobSwagger>(url, apiOptions);

        const jobUrl = (<Record<string, string>>deleteDeploymentJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];
        await createOperationCall(jobUrl, apiOptions, operationHandle);
    };

    const getAssignedResources = async ({ projectName, nextLink }: { projectName: string; nextLink: string }) => {
        const url =
            nextLink ||
            getUrlBuilder(projectName)
                .resource('resources')
                .build();

        const { data: resources } = await httpService.get<ResourceListResults<ResourceAssignmentForRegionSwagger>>(url, apiOptions);

        return resources;
    };

    const addResourceAssignments = async ({
        projectName,
        assignedResources,
        operationHandle
    }: {
        projectName: string;
        assignedResources: ResourceMetadataSwagger[];
        operationHandle: OperationHandler<DeployJobSwagger>;
    }) => {
        const url = getUrlBuilder(projectName)
            .resource('resources/:assign')
            .build();

        const { headers: resourceAssignmentJobHeaders } = await httpService.post<void>(
            url,
            { resourcesMetadata: assignedResources },
            apiOptions
        );

        const jobUrl = (<Record<string, string>>resourceAssignmentJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];
        await createOperationCall(jobUrl, apiOptions, operationHandle);
    };

    const removeResourceAssignments = async ({
        projectName,
        assignedResources,
        operationHandle
    }: {
        projectName: string;
        assignedResources: string[];
        operationHandle: OperationHandler<DeployJobSwagger>;
    }) => {
        const url = getUrlBuilder(projectName)
            .resource('resources/:unassign')
            .build();

        const { headers: resourceAssignmentJobHeaders } = await httpService.post<void>(
            url,
            { assignedResourceIds: assignedResources },
            apiOptions
        );

        const jobUrl = (<Record<string, string>>resourceAssignmentJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];
        await createOperationCall(jobUrl, apiOptions, operationHandle);
    };

    return {
        deploy,
        getDeployment,
        getDeployments,
        swapDeployments,
        deleteDeployment,
        getAssignedResources,
        addResourceAssignments,
        removeResourceAssignments
    };
});
